export const STATUS_STYLES = {
  Draft: { bg: "#F3F4F6", border: "#D1D5DB", text: "#4B5563", dot: "#9CA3AF" },
  Published: { bg: "#EFF6FF", border: "#BFDBFE", text: "#1E40AF", dot: "#3B82F6" },
  "In Progress": { bg: "#FEF3C7", border: "#FCD34D", text: "#92400E", dot: "#D97706" },
  Completed: { bg: "#ECFDF5", border: "#A7F3D0", text: "#065F46", dot: "#10B981" },
  Cancelled: { bg: "#FEF2F2", border: "#FECACA", text: "#991B1B", dot: "#EF4444" },
  Archived: { bg: "#F9FAFB", border: "#E5E7EB", text: "#6B7280", dot: "#9CA3AF" },
};

export function statusStyle(s) {
  return STATUS_STYLES[s] || STATUS_STYLES.Draft;
}

// Derive a display label that includes assignment state.
// status is the workflow stage (Draft/Published/…), worker is the support_worker value.
export function displayStatus(status, worker) {
  const assigned = !!worker;
  if (status === "In Progress" || status === "Completed" || status === "Cancelled" || status === "Archived") return status;
  if (assigned) return status === "Published" ? "Assigned" : "Draft · Assigned";
  return status; // "Draft" or "Published"
}

// Style for the display status — assigned shifts get a distinct teal style.
export const ASSIGNED_STYLE = { bg: "#F0FDFA", border: "#99F6E4", text: "#115E59", dot: "#0D9488" };

export function displayStatusStyle(status, worker) {
  if (!!worker && ["Draft", "Published"].includes(status)) return ASSIGNED_STYLE;
  return statusStyle(status);
}

// Roster board colour palette (per Prydes spec).
export const BOARD_COLOURS = {
  deep_red: { bg: "#FEE2E2", border: "#EF4444", text: "#991B1B", dot: "#DC2626", label: "Published — urgent issue" },
  light_red: { bg: "#FEF9C3", border: "#DC2626", bw: 3, text: "#854D0E", dot: "#DC2626", label: "Unpublished — has a problem" },
  yellow: { bg: "#FEF9C3", border: "#FDE047", text: "#854D0E", dot: "#EAB308", label: "Unpublished" },
  orange: { bg: "#FFEDD5", border: "#FB923C", text: "#9A3412", dot: "#F97316", label: "Published — no worker" },
  green: { bg: "#DCFCE7", border: "#86EFAC", text: "#166534", dot: "#22C55E", label: "Published — no issues" },
  light_blue: { bg: "#E0F2FE", border: "#7DD3FC", text: "#075985", dot: "#38BDF8", label: "Approved" },
  dark_blue: { bg: "#DBEAFE", border: "#1D4ED8", text: "#1E3A8A", dot: "#1D4ED8", label: "Invoiced" },
  grey: { bg: "#F3F4F6", border: "#D1D5DB", text: "#6B7280", dot: "#9CA3AF", label: "Cancelled (no charge)" },
  purple: { bg: "#F5F3FF", border: "#C4B5FD", text: "#6D28D9", dot: "#8B5CF6", label: "Cancelled (with charge)" },
};

// Legend order shown on the board.
export const BOARD_LEGEND = ["deep_red", "light_red", "yellow", "orange", "green", "light_blue", "dark_blue", "grey", "purple"];

// Resolve a shift's board colour key. `hasIssue` is computed on the board from
// live leave / unavailability / double-booking data.
export function boardStateKey(s, hasIssue) {
  const published = !!s.published || ["Published", "In Progress", "Completed"].includes(s.status);
  if (s.status === "Cancelled") {
    return s.cancel_charge ? "purple" : "grey";
  }
  if (s.status === "Archived") return "grey";
  if (s.billing_status === "Finalised") return "dark_blue";
  if (s.timesheet_status === "Approved") return "light_blue";
  const noWorker = !s.support_worker && !(s.worker_count > 0);
  if (published && hasIssue) return "deep_red";
  if (!published && hasIssue) return "light_red";
  if (published && noWorker) return "orange";
  if (published) return "green";
  return "yellow";
}

export function boardStyle(s, hasIssue) {
  return BOARD_COLOURS[boardStateKey(s, hasIssue)];
}

export const TS_STYLES = {
  "Not Started": "#9CA3AF",
  "Pending Review": "#D97706",
  Approved: "#10B981",
  Flagged: "#EF4444",
};

export const BILLING_STYLES = {
  "Not Ready": "#9CA3AF",
  Pending: "#D97706",
  Adjusted: "#3B82F6",
  Finalised: "#10B981",
};

export const RULE_LABELS = {
  document_expiry: "Document expiry / status",
  document_expiry_soft: "Expired (non-critical) document",
  missing_docs: "Missing compliance documents",
  service_agreement: "Service agreement mismatch",
  incompatible_staff: "Incompatible staff",
  double_booking: "Double booking",
  leave_overlap: "Leave overlap",
  maybe_available: "Maybe-available worker",
  travel_time: "Travel-time conflict",
  overtime: "Overtime / workload",
  km_over: "KM over authorised",
  plan_expiry: "NDIS plan expiry",
  long_shift: "Long continuous shift",
  missing_worker: "No worker assigned",
};

export const RULE_DESCRIPTIONS = {
  document_expiry: "Flags when the assigned worker has a required compliance document (e.g. First Aid, Police Check, WWCC) that is expired or invalid for the shift date.",
  missing_docs: "Flags when the worker is missing a compliance document the role requires.",
  service_agreement: "Flags when the shift date falls outside the participant's active NDIS service agreement, or the billing type isn't covered by it.",
  incompatible_staff: "Flags when the assigned worker is on the participant's (or SIL house's) incompatible-staff list and shouldn't work with them.",
  double_booking: "Flags when the same worker is already rostered on another overlapping shift at the same time.",
  leave_overlap: "Flags when the worker has approved leave that overlaps this shift's date/time.",
  unavailability: "Flags when the shift falls inside the worker's ongoing recurring unavailability pattern.",
  maybe_available: "Flags when the worker marked themselves only 'maybe available' for this period — confirm they were contacted and agreed.",
  travel_time: "Warns when the gap to the worker's adjacent same-day shift is tight — if the first runs over they may be late to the next.",
  travel_block: "Blocks when there isn't enough time to physically drive between two of the worker's same-day shifts (gap is shorter than the estimated drive).",
  cas_midnight: "Blocks Community Access shifts running between midnight and 6am — CAS supports can't be delivered overnight.",
  overtime: "Warns when assigning this shift pushes the worker over the configured weekly hours threshold.",
  km_over: "Warns when the kilometres claimed exceed the amount authorised on the shift.",
  plan_expiry: "Warns when the participant's NDIS plan expires on or before the shift date.",
  long_shift: "Warns when a single continuous shift exceeds the maximum-continuous-hours threshold.",
  missing_worker: "Flags a published shift that has no support worker assigned (it's sitting in the unassigned/open queue).",
};
